import { useState } from 'react';
import { clsx } from 'clsx';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetKnowledgeBaseQuery } from 'src/services/knowledgeBaseService';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { useAppSelector } from 'src/store';
import { SidebarDocIcon } from 'src/assets/icons/sidebar/sidebarDocIcon';
import { SidebarArrowIcon } from 'src/assets/icons/sidebar/sidebarArrowIcon';
import { SidebarPlusSquareIcon } from 'src/assets/icons/sidebar/sidebarPlusSquareIcon';
import { CreateEditAiKnowledgeBaseModal } from 'src/pages/AIKnowledgeBasePage/CreateEditAiKnowledgeBase/CreateEditAiKnowledgeBaseModal';

const KNOWLEDGE_BASES_ITEM = 'AI Knowledge Bases';
const KNOWLEDGE_BASE_ACCENT_HEX = '#14CB4C';
const KNOWLEDGE_BASE_ACCENT_CLASS = 'text-[#14CB4C]';

const getInitials = (name: string) =>
  name.trim().split(/\s+/).length > 1
    ? name
        .trim()
        .split(/\s+/)
        .map((word) => word[0])
        .slice(0, 2)
        .join('')
    : name.trim().slice(0, 2);

export const KnowledgeBaseItems = ({ className = '' }: { className?: string }) => {
  const { data: knowledgeBaseData } = useGetKnowledgeBaseQuery({ page_size: 100 });
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const knowledgeBases = knowledgeBaseData?.items || [];
  const { id } = useParams();
  const { storageMenuOpen } = useAppSelector((s) => s.menuSlices);
  const { isInactivePlan } = useAppSelector((s) => s.planSlice);
  const [isExpanded, setIsExpanded] = useState(true);

  const isActiveTab = knowledgeBases.some((kb) => kb.id === +id);
  const isKbListSelected = pathname === PATH_DASHBOARD.AI_KNOWLEDGE_BASE;

  return (
    <div className={clsx(storageMenuOpen ? 'px-6' : 'px-4', 'py-5 min-w-[70px]', className)}>
      <Accordion
        type="single"
        collapsible
        className="w-full"
        value={isExpanded ? KNOWLEDGE_BASES_ITEM : ''}
        onValueChange={(value) => setIsExpanded(value === KNOWLEDGE_BASES_ITEM)}
      >
        <AccordionItem value={KNOWLEDGE_BASES_ITEM} className="border-b-0">
          <div
            className={clsx(
              isKbListSelected || isActiveTab || isExpanded
                ? '[&_p]:opacity-100'
                : '[&_p]:opacity-60',
              isKbListSelected && 'bg-[#2F2F33]',
              'flex items-center rounded-sm-5 duration-300 [&_p]:transition-all [&_p]:hover:opacity-100',
            )}
          >
            <button
              type="button"
              onClick={() => navigate(PATH_DASHBOARD.AI_KNOWLEDGE_BASE)}
              className="flex flex-1 items-center gap-1 text-left outline-none"
            >
              <div className="p-2">
                <SidebarDocIcon />
              </div>
              <p
                className={clsx(
                  !storageMenuOpen && '!opacity-0',
                  'transition-all absolute left-16 duration-300 text-white text-sm font-medium text-ellipsis overflow-hidden text-nowrap',
                )}
              >
                {KNOWLEDGE_BASES_ITEM}
              </p>
            </button>
            <AccordionTrigger
              hideDefaultIcon
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${KNOWLEDGE_BASES_ITEM}`}
              className={clsx(
                'flex-none py-0 pr-2 [&_svg]:transition-all [&_svg]:opacity-50 [&_svg]:hover:opacity-100 [&[data-state=open]_svg]:opacity-100',
                isKbListSelected && '[&_svg]:!opacity-100',
                !storageMenuOpen && '[&_.chevron]:!opacity-0',
              )}
            >
              <SidebarArrowIcon className="chevron transition-transform duration-200" />
            </AccordionTrigger>
          </div>
          <AccordionContent className="pb-0">
            <div className="mt-2 flex flex-col gap-1.5 relative">
              {knowledgeBases.map((kb) => {
                const isKbLocation = pathname.startsWith(PATH_DASHBOARD.AI_KNOWLEDGE_BASE);
                const isSameId = +id === kb.id;
                const activeSlide = isKbLocation && isSameId;
                const cutName = getInitials(kb.name);

                return (
                  <Link
                    key={kb.id}
                    className={clsx(
                      storageMenuOpen && 'pl-11 pr-2.5',
                      activeSlide && 'bg-[#1E1E25]',
                      'p-1 rounded-sm-5 overflow-hidden transition-all duration-300 [&_span]:hover:opacity-100 [&_span]:transition-all relative',
                    )}
                    to={`${PATH_DASHBOARD.AI_KNOWLEDGE_BASE}/${kb.id}`}
                  >
                    <div
                      className={clsx(
                        activeSlide ? '[&_span]:opacity-100' : '[&_span]:opacity-60',
                        'transition-all duration-300',
                      )}
                    >
                      <button
                        className={clsx(
                          !storageMenuOpen && 'bg-white bg-opacity-10',
                          storageMenuOpen && 'truncate',
                          'p-1.5 text-left max-w-full rounded-sm-5 min-w-[30px]',
                        )}
                        title={kb.name}
                      >
                        <span
                          className={clsx(
                            'text-white',
                            activeSlide && 'font-bold',
                            'font-medium text-sm',
                          )}
                        >
                          {storageMenuOpen ? kb.name : cutName}
                        </span>
                      </button>
                    </div>
                  </Link>
                );
              })}
              <CreateEditAiKnowledgeBaseModal
                disabled={isInactivePlan}
                trigger={
                  <button
                    type="button"
                    className={clsx(
                      isInactivePlan && 'pointer-events-none',
                      storageMenuOpen && 'pl-2.5 pr-2.5',
                      'p-1 w-full text-left opacity-70 hover:opacity-100 transition-all duration-300',
                    )}
                  >
                    <div
                      className={clsx(
                        isInactivePlan && 'pointer-events-none opacity-20',
                        !storageMenuOpen && 'bg-white bg-opacity-10 p-2',
                        'flex min-w-[30px] min-h-[30px] items-center justify-start p-1.5 gap-2 rounded-sm-5 transition-all duration-300',
                      )}
                    >
                      <SidebarPlusSquareIcon color={KNOWLEDGE_BASE_ACCENT_HEX} />
                      <span
                        className={clsx(
                          !storageMenuOpen && '!opacity-0',
                          KNOWLEDGE_BASE_ACCENT_CLASS,
                          'transition-all absolute left-[40px] duration-300 text-[13px] font-semibold',
                        )}
                      >
                        Add Knowledge Base
                      </span>
                    </div>
                  </button>
                }
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
